import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import { CATEGORIES } from '@/lib/categories'
import { formatDate, formatCurrency } from '@/lib/utils'
import ExpiryBadge from '@/components/ExpiryBadge'
import ItemForm from '@/components/ItemForm'
import AttachmentList from '@/components/AttachmentList'
import OneDrivePicker from '@/components/OneDrivePicker'
import type { OneDriveFile } from '@/types'

export default function ItemDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [pickingOneDrive, setPickingOneDrive] = useState(false)
  const [oneDriveCallback, setOneDriveCallback] = useState<((f: OneDriveFile & { driveId: string }) => Promise<void>) | null>(null)

  const { data: item, isLoading } = useQuery({
    queryKey: ['item', id],
    queryFn: () => api.items.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.items.update>[1]) => api.items.update(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item', id] })
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.items.delete(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(-1)
    },
  })

  if (isLoading) return <div className="animate-pulse h-40 bg-white rounded-xl border border-slate-200" />
  if (!item) return <p className="text-slate-500 text-sm">Record not found.</p>

  const catConfig = CATEGORIES[item.category]
  const subConfig = catConfig?.subcategories[item.subcategory]

  const metadataEntries = Object.entries(item.metadata ?? {}).filter(([, v]) => v != null && v !== '')

  return (
    <div className="space-y-4">
      {/* Back + actions */}
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1" />
        <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
          <Pencil size={16} />
        </button>
        <button
          onClick={() => setConfirmDelete(true)}
          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-500"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Main card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{catConfig?.label} · {subConfig?.label}</p>
            <h2 className="text-lg font-semibold text-slate-900 mt-0.5">{item.title}</h2>
            {item.provider && <p className="text-sm text-slate-500 mt-0.5">{item.provider}</p>}
          </div>
          <ExpiryBadge expiryDate={item.expiry_date} />
        </div>

        <dl className="grid grid-cols-2 gap-3">
          {item.reference_number && <Field label="Reference #" value={item.reference_number} />}
          {item.amount && (
            <Field label="Amount" value={`${formatCurrency(item.amount)}${item.amount_frequency ? ` / ${item.amount_frequency}` : ''}`} />
          )}
          {item.start_date && <Field label="Start Date" value={formatDate(item.start_date)} />}
          {item.expiry_date && <Field label="Expiry / Due" value={formatDate(item.expiry_date)} />}
          {metadataEntries.map(([key, value]) => {
            const fieldConfig = subConfig?.fields.find(f => f.key === key)
            return <Field key={key} label={fieldConfig?.label ?? key} value={String(value)} />
          })}
        </dl>

        {item.notes && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-1">Notes</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.notes}</p>
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <AttachmentList
          itemId={item.id}
          onPickOneDrive={(cb) => {
            setOneDriveCallback(() => cb)
            setPickingOneDrive(true)
          }}
        />
      </div>

      {editing && (
        <ItemForm
          initial={item}
          onSave={async (data) => { await updateMutation.mutateAsync(data) }}
          onClose={() => setEditing(false)}
        />
      )}

      {pickingOneDrive && oneDriveCallback && (
        <OneDrivePicker
          onSelect={oneDriveCallback}
          onClose={() => { setPickingOneDrive(false); setOneDriveCallback(null) }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-sm md:rounded-2xl rounded-t-2xl p-6 space-y-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Delete record?</h3>
              <p className="text-sm text-slate-500 mt-1">
                <span className="font-medium text-slate-700">{item.title}</span> and all its attachments will be permanently deleted. This cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="text-sm font-medium text-slate-800 mt-0.5">{value}</dd>
    </div>
  )
}
